import { useState } from 'react'
import { useTrackerQualityHistory } from '../../api/hooks/useTrackerQuality'

const QUALITY_COLORS = { hot: '#ef4444', warm: '#eab308', cold: '#3b82f6' }
const QUALITY_LABELS: Record<string, string> = { hot: 'Quente', warm: 'Morno', cold: 'Frio' }

function QualityBadge({ quality }: { quality: 'hot' | 'warm' | 'cold' }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: QUALITY_COLORS[quality] + '20', color: QUALITY_COLORS[quality] }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: QUALITY_COLORS[quality] }} />
      {QUALITY_LABELS[quality]}
    </span>
  )
}

function DistributionBar({ hot, warm, cold }: { hot: number; warm: number; cold: number }) {
  const total = hot + warm + cold
  if (total === 0) return <div className="h-3 bg-zinc-800 rounded-full" />
  const hp = (hot / total) * 100
  const wp = (warm / total) * 100
  return (
    <div className="flex h-3 rounded-full overflow-hidden">
      <div style={{ width: `${hp}%`, backgroundColor: QUALITY_COLORS.hot }} />
      <div style={{ width: `${wp}%`, backgroundColor: QUALITY_COLORS.warm }} />
      <div style={{ width: `${100 - hp - wp}%`, backgroundColor: QUALITY_COLORS.cold }} />
    </div>
  )
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m${s.toString().padStart(2, '0')}s` : `${s}s`
}

export default function QualityTab({ siteId }: { siteId: string }) {
  const [days, setDays] = useState(30)
  const [qualityFilter, setQualityFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [page, setPage] = useState(1)
  const [showSessions, setShowSessions] = useState(false)

  const { data, isLoading } = useTrackerQualityHistory(siteId, days, {
    quality: qualityFilter || undefined,
    source: sourceFilter || undefined,
    page,
  })

  if (isLoading) return <div className="text-zinc-500 py-8 text-center">Carregando...</div>
  if (!data) return <div className="text-zinc-500 py-8 text-center">Sem dados de qualidade ainda. Os dados aparecem após o primeiro flush (a cada 5 min).</div>

  const { summary, daily, by_source, sessions } = data

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex gap-2">
        {[7, 14, 30, 60].map(d => (
          <button
            key={d}
            onClick={() => { setDays(d); setPage(1) }}
            className={`px-3 py-1 rounded text-sm ${days === d ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Engagement Rate</div>
          <div className="text-2xl font-bold text-white">{summary.engagement_rate}%</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Score Médio</div>
          <div className="text-2xl font-bold" style={{ color: summary.avg_score >= 70 ? QUALITY_COLORS.hot : summary.avg_score >= 40 ? QUALITY_COLORS.warm : QUALITY_COLORS.cold }}>
            {summary.avg_score}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Distribuição ({summary.total_sessions} sessões)</div>
          <DistributionBar hot={summary.hot_pct} warm={summary.warm_pct} cold={summary.cold_pct} />
          <div className="flex justify-between mt-2 text-xs text-zinc-400">
            <span style={{ color: QUALITY_COLORS.hot }}>{summary.hot_pct}% quente</span>
            <span style={{ color: QUALITY_COLORS.warm }}>{summary.warm_pct}% morno</span>
            <span style={{ color: QUALITY_COLORS.cold }}>{summary.cold_pct}% frio</span>
          </div>
        </div>
      </div>

      {/* Daily chart */}
      {daily.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-medium text-zinc-400 mb-3">Evolução diária</h3>
          <div className="flex items-end gap-1 h-32">
            {daily.map((d, i) => {
              const maxSess = Math.max(...daily.map(x => Number(x.sessions)), 1)
              const h = (Number(d.sessions) / maxSess) * 100
              const scoreColor = Number(d.avg_score) >= 70 ? QUALITY_COLORS.hot : Number(d.avg_score) >= 40 ? QUALITY_COLORS.warm : QUALITY_COLORS.cold
              return (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div className="hidden group-hover:block absolute -top-16 bg-zinc-800 text-xs text-white p-2 rounded shadow z-10 whitespace-nowrap">
                    <div>{d.date}</div>
                    <div>{d.sessions} sessões · score {d.avg_score}</div>
                    <div>eng. {d.engagement_rate}%</div>
                  </div>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(h, 2)}%`, backgroundColor: scoreColor, opacity: 0.8 }} />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Source quality table */}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-left border-b border-border">
              <th className="p-3">Fonte</th>
              <th className="p-3 text-right">Sessões</th>
              <th className="p-3 text-right">Eng. Rate</th>
              <th className="p-3 text-right">Score</th>
              <th className="p-3 w-32">Distribuição</th>
              <th className="p-3 text-right">CTA Rate</th>
              <th className="p-3 text-right">Tempo</th>
            </tr>
          </thead>
          <tbody>
            {by_source.map((src, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => { setSourceFilter(src.source_label.split(' / ')[0]); setShowSessions(true); setPage(1) }}>
                <td className="p-3 text-white font-medium">{src.source_label}</td>
                <td className="p-3 text-right text-zinc-300">{src.total_sessions}</td>
                <td className="p-3 text-right text-zinc-300">{src.engagement_rate}%</td>
                <td className="p-3 text-right font-medium" style={{ color: Number(src.avg_score) >= 70 ? QUALITY_COLORS.hot : Number(src.avg_score) >= 40 ? QUALITY_COLORS.warm : QUALITY_COLORS.cold }}>
                  {src.avg_score}
                </td>
                <td className="p-3">
                  <DistributionBar hot={Number(src.hot_pct)} warm={Number(src.warm_pct)} cold={Number(src.cold_pct)} />
                </td>
                <td className="p-3 text-right text-zinc-300">{src.cta_rate}%</td>
                <td className="p-3 text-right text-zinc-300">{formatDuration(Number(src.avg_duration))}</td>
              </tr>
            ))}
            {by_source.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Nenhum dado de qualidade ainda</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sessions list */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <button onClick={() => setShowSessions(!showSessions)} className="text-sm font-medium text-zinc-400 hover:text-white">
            {showSessions ? '▼' : '▶'} Sessões individuais ({sessions.total})
          </button>
          <div className="flex gap-2">
            <select value={qualityFilter} onChange={e => { setQualityFilter(e.target.value); setPage(1) }} className="bg-zinc-800 text-xs text-zinc-300 rounded px-2 py-1 border border-zinc-700">
              <option value="">Todas</option>
              <option value="hot">Quentes</option>
              <option value="warm">Mornos</option>
              <option value="cold">Frios</option>
            </select>
            {sourceFilter && (
              <button onClick={() => { setSourceFilter(''); setPage(1) }} className="text-xs text-zinc-400 hover:text-white bg-zinc-800 px-2 py-1 rounded border border-zinc-700">
                × {sourceFilter}
              </button>
            )}
          </div>
        </div>

        {showSessions && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-left border-b border-border">
                  <th className="p-3">Visitor</th>
                  <th className="p-3">Página</th>
                  <th className="p-3 text-right">Duração</th>
                  <th className="p-3 text-right">Páginas</th>
                  <th className="p-3 text-right">Scroll</th>
                  <th className="p-3 text-center">CTA</th>
                  <th className="p-3 text-right">Score</th>
                  <th className="p-3">Qualidade</th>
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="p-3 text-zinc-400 font-mono text-xs">{s.visitor_id.slice(0, 8)}</td>
                    <td className="p-3 text-zinc-300 text-xs max-w-[200px] truncate">{s.entry_page || '—'}</td>
                    <td className="p-3 text-right text-zinc-300">{formatDuration(s.duration_seconds)}</td>
                    <td className="p-3 text-right text-zinc-300">{s.pages_count}</td>
                    <td className="p-3 text-right text-zinc-300">{s.max_scroll}%</td>
                    <td className="p-3 text-center">{s.cta_clicked ? '✓' : '—'}</td>
                    <td className="p-3 text-right font-medium" style={{ color: QUALITY_COLORS[s.quality] }}>{s.score}</td>
                    <td className="p-3"><QualityBadge quality={s.quality} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sessions.last_page > 1 && (
              <div className="flex justify-center gap-2 p-3">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded bg-zinc-800 text-zinc-400 text-sm disabled:opacity-30">←</button>
                <span className="text-sm text-zinc-500 py-1">{sessions.current_page} / {sessions.last_page}</span>
                <button disabled={page >= sessions.last_page} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded bg-zinc-800 text-zinc-400 text-sm disabled:opacity-30">→</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
